import { useCurrentLineData } from 'modules/Editor/components/FlowCore/components/PortStepsGrid.hooks';
import { useCallback } from 'react';
import { DEFAULT_MARGIN_STEPPED_LINE } from 'widgets/Lines/LineWidget/LineWidget.config';
import { calculateDeltaPoint } from 'widgets/Lines/LineWidget/LineWidget.helpers';
import { CommonLineProps } from 'widgets/Lines/LineWidget/LineWidget.types';
import { generateStepsPath, roundPos } from 'widgets/Lines/LineWidget/StepsLineWidget/StepsLineWidget.helpers';

interface UseSteppedLinePosListProps
  extends Pick<
    CommonLineProps,
    | 'posList'
    | 'delta'
    | 'startPort'
    | 'endPort'
    | 'leftPx'
    | 'topPx'
    | 'realTimeStartRect'
    | 'realTimeEndRect'
    | 'realTimeStartPort'
    | 'realTimeEndPort'
    | 'isUseRealTimeStartRect'
    | 'isUseRealTimeEndRect'
  > {}

/**
 * Hook to generate the pos list for the stepped line connecting 2 widgets
 *
 * @returns () => Pos[] - Function to compute the pos list
 */
export const useSteppedLinePosList = ({
  posList,
  delta,
  startPort,
  endPort,
  leftPx = 0,
  topPx = 0,
  realTimeStartRect,
  realTimeEndRect,
  realTimeStartPort,
  realTimeEndPort,
  isUseRealTimeStartRect,
  isUseRealTimeEndRect,
}: UseSteppedLinePosListProps) => {
  const { relativeStartRect, relativeEndRect, startSide, endSide } = useCurrentLineData(
    {
      startPort,
      endPort,
      leftPx,
      topPx,
      posList,
    },
    realTimeStartRect,
    realTimeEndRect,
    realTimeStartPort,
    realTimeEndPort,
    isUseRealTimeStartRect,
    isUseRealTimeEndRect,
  );

  const computeNewPosList = useCallback(() => {
    if (delta?.targetIndex !== undefined && delta?.targetIndex >= 0) {
      // Calculate the new pos list accounting delta changes
      const deltaChangePosList = calculateDeltaPoint(posList, delta);
      const temp = generateStepsPath(
        roundPos(deltaChangePosList[0]),
        roundPos(deltaChangePosList[deltaChangePosList.length - 1]),
        startSide,
        endSide,
        relativeStartRect,
        relativeEndRect,
        DEFAULT_MARGIN_STEPPED_LINE,
      );
      if (temp.length <= 1) {
        return deltaChangePosList;
      }
      return temp;
    }
    if (posList.length >= 2) {
      const temp = generateStepsPath(
        roundPos(posList[0]),
        roundPos(posList[posList.length - 1]),
        startSide,
        endSide,
        relativeStartRect,
        relativeEndRect,
        DEFAULT_MARGIN_STEPPED_LINE,
      );
      if (temp.length <= 1) {
        return posList;
      }
      return temp;
    }
    return posList;
  }, [posList, delta, relativeStartRect, relativeEndRect, startSide, endSide]);

  return computeNewPosList;
};
